import { faCircleInfo } from "@fortawesome/free-solid-svg-icons/faCircleInfo";
import dayjs from "dayjs";
import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraIcon } from "../../../../../components/AtiraIcon";
import { integrationSliceSelectors } from "../../../../../redux/integration/integration.selector";
import { useAppSelector } from "../../../../../redux/store";
import { userSliceSelectors } from "../../../../../redux/user/user.selector";
import { Rounded } from "../../../../../theme/Rounded";
import { Spacing } from "../../../../../theme/Spacing";
import { standardDate } from "../../../../../utils/Date";
import { Tooltip } from "../../../../../utils/local-antd";

const Overlay = styled.span`
  position: absolute;
  top: ${Spacing.xs};
  inset-inline-end: ${Spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.4rem;
  height: 1.4rem;
  border-radius: ${Rounded.circle};
  background-color: rgba(0, 0, 0, 0.45);
  color: #fff;
  line-height: 1;
  cursor: help;
`;

const Inline = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  opacity: 0.6;
  cursor: help;
`;

type Props = {
  createdAt: Date | string;
  /** overlay sits in the corner of a picture or video, which needs a
   * positioned parent; inline sits in the row of a document or voice note
   */
  variant?: "overlay" | "inline";
};

export const WAMediaRetentionInfo: React.FC<Props> = ({
  createdAt,
  variant = "overlay",
}) => {
  const { t } = useTranslation();

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser);
  const retentionDays = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaMediaStorage,
  )?.retentionDays;

  if (!retentionDays || !createdAt) {
    return null;
  }

  const removedOn = dayjs(createdAt).add(retentionDays, "day").toDate();

  // Most of these sit on something clickable - a video poster, a download
  // link - that should not fire when someone only wanted the tooltip
  const swallow = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const Wrapper = variant === "overlay" ? Overlay : Inline;

  return (
    <Tooltip
      title={t("integration.whatsapp.meta.media.retention", {
        date: standardDate<string>(removedOn, user),
        months: Math.round(retentionDays / 30),
      })}
    >
      <Wrapper onClick={swallow}>
        <AtiraIcon
          icon={faCircleInfo}
          size="xs"
          color={variant === "overlay" ? "white" : undefined}
        />
      </Wrapper>
    </Tooltip>
  );
};
